import React, { useEffect, useState, useRef, useCallback } from "react";
import { Web } from "sip.js";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Container,
  CssBaseline,
  Fab,
  CircularProgress,
  Paper,
  IconButton,
  Fade,
  Alert,
  Stack
} from '@mui/material';
import {
  Phone as PhoneIcon,
  PhoneDisabled as PhoneDisabledIcon,
  Dialpad as DialpadIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon
} from '@mui/icons-material';
import { BcallLogo } from './components/BcallLogo'; // Import the logo component

// --- Types ---
type CallState = 'idle' | 'connecting' | 'registered' | 'calling' | 'incall' | 'error';

interface SipCredentials {
  user: string;
  pass: string;
  turnUser: string;
  turnPass: string;
  targetSipUser: string;
  domain: string;
  websocket: string;
  sessionId: string;
  sessionToken: string;
  targetNumber?: string;
  extension?: string;
}

const API_BASE_URL = "https://dialer1.bcall.us";

async function deleteSession(creds: SipCredentials | null) {
  if (!creds) return;
  await fetch(`${API_BASE_URL}/call-sessions/${creds.sessionId}`, {
    method: 'DELETE',
    headers: { 'x-session-token': creds.sessionToken }
  }).catch(() => {});
}

async function waitUntilReady(creds: SipCredentials) {
  const deadline = Date.now() + 30_000;
  while (Date.now() < deadline) {
    const response = await fetch(`${API_BASE_URL}/call-sessions/${creds.sessionId}/status?token=${encodeURIComponent(creds.sessionToken)}`);
    if (response.ok && (await response.json()).status === 'ready') return;
    await new Promise(resolve => setTimeout(resolve, 750));
  }
  throw new Error('The extension did not become ready in time');
}

// --- Logic Hook: useSipClient ---
// כלי זה מבודד את כל ההתעסקות עם ספריית SIP
const useSipClient = (displayName: string = "Bcall User") => {
  const [callState, setCallState] = useState<CallState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const simpleUserRef = useRef<Web.SimpleUser | null>(null);
  const credentialsRef = useRef<SipCredentials | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // פונקציית עזר למציאת אלמנט האודיו
  const getAudioElement = (): HTMLAudioElement => {
    if (!audioRef.current) {
      const el = document.getElementById("remoteAudio");
      if (el instanceof HTMLAudioElement) {
        audioRef.current = el;
      } else {
        throw new Error("Remote audio element not found");
      }
    }
    return audioRef.current;
  };

  const connectAndCall = useCallback(async (creds: SipCredentials) => {
    try {
      setCallState('connecting');
      setErrorMsg(null);
      credentialsRef.current = creds;
      
      const options: Web.SimpleUserOptions = {
        aor: `sip:${creds.user}@${creds.domain}`,
        media: {
          remote: { audio: getAudioElement() }
        },
        userAgentOptions: {
            displayName,
            authorizationPassword: creds.pass,
            authorizationUsername: creds.user,
            sessionDescriptionHandlerFactoryOptions: {
                peerConnectionConfiguration: {
                    iceServers: [
                        { urls: ["stun:turn.bcall.us"] },
                        {
                            username: creds.turnUser,
                            credential: creds.turnPass,
                            urls: ["turn:turn.bcall.us:3478"]
                        }
                    ]
                }
            }
        }
      };

      const simpleUser = new Web.SimpleUser(creds.websocket, options);
      simpleUserRef.current = simpleUser;

      // הגדרת מאזינים לאירועים (Delegates)
      simpleUser.delegate = {
        onCallAnswered: () => {
          setCallState('incall');
          // אם יש שלוחה מוגדרת, שלח אותה אוטומטית
          if (false) {
            const extWithHash = creds.extension + "#";
            // השהיה קטנה לפני שליחת DTMF כדי לוודא חיבור
            setTimeout(() => {
                extWithHash.split('').forEach(char => simpleUser.sendDTMF(char));
            }, 500);
          }
        },
        onCallHangup: () => {
          setCallState('idle');
          simpleUser.unregister().catch(() => {});
          simpleUser.disconnect();
          deleteSession(credentialsRef.current);
        },
        onCallReceived: () => {
            // טיפול בשיחה נכנסת אם צריך בעתיד
        }
      };

      await simpleUser.connect();
      await simpleUser.register();
      setCallState('registered');

      // ביצוע השיחה
      setCallState('calling');
      await waitUntilReady(creds);
      await simpleUser.call(`sip:${creds.targetSipUser}@${creds.domain}`);

    } catch (err: any) {
      console.error("SIP Error:", err);
      setCallState('error');
      setErrorMsg(err.message || "Connection failed");
    }
  }, [displayName]);

  const hangup = useCallback(async () => {
    if (simpleUserRef.current) {
      await simpleUserRef.current.hangup().catch(() => {});
      await simpleUserRef.current.unregister().catch(() => {});
      await simpleUserRef.current.disconnect().catch(() => {});
      setCallState('idle');
    }
    await deleteSession(credentialsRef.current);
    credentialsRef.current = null;
  }, []);

  const sendDTMF = useCallback((tone: string) => {
    if (simpleUserRef.current && callState === 'incall') {
      simpleUserRef.current.sendDTMF(tone);
    }
  }, [callState]);

  // ניקוי ביציאה
  useEffect(() => {
    return () => {
      if (simpleUserRef.current) {
        simpleUserRef.current.unregister();
        simpleUserRef.current.disconnect();
      }
      deleteSession(credentialsRef.current);
    };
  }, []);

  return { callState, errorMsg, connectAndCall, hangup, sendDTMF };
};

// --- Modern UI Component ---
export default function Dialer() {
  const navigate = useNavigate();
  const { callState, errorMsg, connectAndCall, hangup, sendDTMF } = useSipClient();
  const [dialPadOpen, setDialPadOpen] = useState(false);

  // טעינת נתונים מה-Session
  const handleStartCall = async () => {
    const destinationName = sessionStorage.getItem("bcallTargetName");
    if (destinationName) {
      try {
        const response = await fetch(`${API_BASE_URL}/call-sessions`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ name: destinationName, callerId: 'Bcall web visitor' })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Unable to start call');
        await connectAndCall({
          user: data.userpass,
          pass: data.password,
          turnUser: data.turn.username,
          turnPass: data.turn.password,
          targetSipUser: data.targetSipUser,
          domain: data.domain,
          websocket: data.websocket,
          sessionId: data.sessionId,
          sessionToken: data.sessionToken
        });
      } catch (error: any) {
        console.error('Unable to start call', error);
        alert(error.message || 'Unable to start call');
      }
      return;
    }
    const user = sessionStorage.getItem("userpass");
    const pass = sessionStorage.getItem("password");
    const turnUser = sessionStorage.getItem("turnuser");
    const turnPass = sessionStorage.getItem("turnpassword");
    const targetNumber = sessionStorage.getItem("numberadv");
    const domain = sessionStorage.getItem("sipDomain");
    const websocket = sessionStorage.getItem("sipServer");
    const sessionId = sessionStorage.getItem("sessionId");
    const sessionToken = sessionStorage.getItem("sessionToken");
    const extension = sessionStorage.getItem("ext") || "";

    if (user && pass && turnUser && turnPass && targetNumber && domain && websocket && sessionId && sessionToken) {
      connectAndCall({
        user,
        pass,
        turnUser,
        turnPass,
        targetNumber,
        extension,
        targetSipUser: targetNumber,
        domain,
        websocket,
        sessionId,
        sessionToken
      });
    } else {
        alert("Missing call-session configuration. Please return to the call page and start a new call.");
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      {/* אלמנט אודיו נסתר חובה ל-WebRTC */}
      <audio id="remoteAudio" autoPlay style={{ display: 'none' }} />

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '32px',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.5)',
            boxShadow: '0 20px 40px -10px rgba(0,0,0,0.1)',
            width: '100%',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Status Indicator Dot */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, bgcolor: '#f1f5f9', px: 2, py: 0.5, borderRadius: 10 }}>
              <Box sx={{ 
                  width: 8, height: 8, borderRadius: '50%', 
                  bgcolor: callState === 'incall' ? '#10b981' : callState === 'calling' ? '#f59e0b' : '#cbd5e1' 
              }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                {callState === 'idle' ? 'STANDBY' : callState.toUpperCase()}
              </Typography>
          </Box>

          <Typography variant="h4" sx={{ mb: 4, fontWeight: 700, color: '#1e293b' }}>
            {callState === 'incall' ? 'In Call' : 'Call Now'}
          </Typography>

          {/* Error Message */}
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2, width: '100%', borderRadius: 3 }}>{errorMsg}</Alert>
          )}

          {/* Call Button Container */}
          <Box sx={{ my: 2, position: 'relative' }}>
             {/* Ring Animation */}
             {callState === 'calling' && (
                <>
                <Box sx={{
                    position: 'absolute', top: -4, left: -4, right: -4, bottom: -4,
                    borderRadius: '50%', border: '1px solid #2979ff',
                    animation: 'pulse 2s infinite'
                }} />
                 <Box sx={{
                    position: 'absolute', top: -12, left: -12, right: -12, bottom: -12,
                    borderRadius: '50%', border: '1px solid rgba(41, 121, 255, 0.3)',
                    animation: 'pulse 2s infinite 0.3s'
                }} />
                </>
             )}
             
             <Fab
                onClick={callState === 'idle' || callState === 'error' ? handleStartCall : hangup}
                sx={{ 
                    width: 80, 
                    height: 80, 
                    fontSize: '2rem',
                    boxShadow: '0 10px 30px rgba(41, 121, 255, 0.3)', // Blue shadow
                    transition: '0.3s',
                    background: callState === 'incall' || callState === 'calling' 
                        ? '#ef5350' // Soft Red
                        : 'linear-gradient(135deg, #2979ff 0%, #1565c0 100%)', // Blue Gradient
                    color: 'white',
                    '&:hover': {
                         transform: 'scale(1.05)',
                         background: callState === 'incall' || callState === 'calling' 
                        ? '#d32f2f' 
                        : 'linear-gradient(135deg, #2979ff 0%, #0d47a1 100%)'
                    }
                }}
             >
                {callState === 'idle' || callState === 'error' ? <PhoneIcon fontSize="large" /> : <PhoneDisabledIcon fontSize="large" />}
             </Fab>
          </Box>

          {/* In-Call Controls */}
          <Fade in={callState === 'incall'}>
            <Stack direction="row" spacing={3} sx={{ mt: 4 }}>
               <IconButton 
                    onClick={() => setDialPadOpen(!dialPadOpen)}
                    sx={{ 
                        color: dialPadOpen ? '#2979ff' : '#64748b', 
                        bgcolor: dialPadOpen ? '#e3f2fd' : '#f1f5f9',
                        p: 1.5,
                        '&:hover': { bgcolor: '#bbdefb' }
                    }}>
                  <DialpadIcon />
               </IconButton>
               <IconButton sx={{ color: '#64748b', bgcolor: '#f1f5f9', p: 1.5 }}>
                  <MicIcon />
               </IconButton>
            </Stack>
          </Fade>
          
           {/* Numeric Keypad (Collapsible) */}
          <Fade in={dialPadOpen && callState === 'incall'}>
            <Box sx={{ mt: 3, width: '100%', bgcolor: '#f8fafc', p: 2, borderRadius: 4 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 1,
                    justifyItems: 'center'
                  }}
                >
                  {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((digit) => (
                    <Fab
                      key={digit}
                      size="small"
                      onClick={() => sendDTMF(digit)}
                      sx={{
                        bgcolor: 'white',
                        color: '#334155',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        '&:hover': { bgcolor: '#f1f5f9' }
                      }}
                    >
                      {digit}
                    </Fab>
                  ))}
                </Box>
            </Box>
          </Fade>

          <Box sx={{ mt: 4, transform: 'scale(0.8)', opacity: 0.7 }}>
             <BcallLogo size={40} />
          </Box>

        </Paper>
      </Box>
      
      {/* CSS for Pulse Animation */}
      <style>{`
        @keyframes pulse {
          0% { transform: scale(0.95); opacity: 0.7; }
          70% { transform: scale(1.1); opacity: 0; }
          100% { transform: scale(0.95); opacity: 0; }
        }
      `}</style>
    </Container>
  );
}
